import React, { useState } from 'react';

interface DraftPick {
    pick_number: number;
    actual_team: string;
    actual_team_name: string;
    player: string;
    larry_line_team: string | null;
    larry_line_team_name: string | null;
}

interface LarryPoolStat {
    points?: number;
    wins?: number;
    record: string;
    games?: number;
    elim_date?: string | null;
}

interface DraftYear {
    season_label: string;
    picks: DraftPick[];
    larry_pool_stats?: Record<string, LarryPoolStat>;
}

interface Props {
    drafts: Record<number, DraftYear>;
    league: string;
    loading: boolean;
}

const DraftComparison: React.FC<Props> = ({ drafts, league, loading }) => {
    const years = Object.keys(drafts).map(Number).sort((a, b) => b - a);
    const [selectedYear, setSelectedYear] = useState<number>(years[0] || 2025);

    if (loading) {
        return <div className="larry-loading">Loading draft history...</div>;
    }

    if (years.length === 0) {
        return <div className="larry-empty">No draft history available.</div>;
    }

    const currentDraft = drafts[selectedYear];
    if (!currentDraft) return null;

    const isNHL = league === 'nhl';
    const poolStats = currentDraft.larry_pool_stats || {};
    const hasPoolGames = Object.values(poolStats).some(s => s.games !== undefined);

    return (
        <div className="draft-comparison">
            <h2>What Would Have Happened? ({league.toUpperCase()} Draft History)</h2>
            <p style={{ color: '#555', marginBottom: 14, fontSize: '0.92rem' }}>
                Compare the actual draft order with what would have happened under The Larry Line system.
                Same players, different teams. {isNHL
                    ? 'Larry Pool ranks by points earned after elimination.'
                    : 'Larry Pool ranks by wins earned after elimination.'}
            </p>

            <div className="draft-year-selector">
                {years.map(year => (
                    <button
                        key={year}
                        className={`draft-year-btn ${year === selectedYear ? 'active' : ''}`}
                        onClick={() => setSelectedYear(year)}
                    >
                        {year}
                    </button>
                ))}
            </div>

            <table className="draft-table">
                <thead>
                    <tr>
                        <th>Pick</th>
                        <th>Player</th>
                        <th>Actual Team</th>
                        <th>Larry Line Team</th>
                        <th>{isNHL ? 'Pool Pts' : 'Wins'}</th>
                        <th>Pool Record</th>
                        {hasPoolGames && <th>GP</th>}
                    </tr>
                </thead>
                <tbody>
                    {currentDraft.picks.map(pick => {
                        const isDifferent = pick.larry_line_team && pick.larry_line_team !== pick.actual_team;
                        const stats = pick.larry_line_team ? poolStats[pick.larry_line_team] : null;
                        return (
                            <tr key={pick.pick_number} className={isDifferent ? 'different' : ''}>
                                <td className="pick-number">#{pick.pick_number}</td>
                                <td style={{ fontWeight: 600 }}>{pick.player}</td>
                                <td>{pick.actual_team_name}</td>
                                <td style={{ fontWeight: isDifferent ? 700 : 400 }}>
                                    {pick.larry_line_team_name || '\u2014'}
                                </td>
                                <td className="num-cell" style={{ fontWeight: 600 }}>
                                    {stats ? (isNHL ? stats.points : stats.wins) : '\u2014'}
                                </td>
                                <td className="num-cell">
                                    {stats ? stats.record : '\u2014'}
                                </td>
                                {hasPoolGames && (
                                    <td className="num-cell">
                                        {stats && stats.games !== undefined ? stats.games : '\u2014'}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default DraftComparison;
